import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';

const VendorDashboard = ({ onNavigate, userInfo }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vendorData, setVendorData] = useState({
    vendor_name: "Loading...",
    vendor_id: "...",
    contact_person: "",
    email: "",
    phone: "",
    company_type: "",
    vendor_status: "active"
  });

  const [dashboardStats, setDashboardStats] = useState({
    totalBids: 0,
    activeBids: 0,
    wonBids: 0,
    lostBids: 0,
    totalTenders: 0
  });

  const [activeTenders, setActiveTenders] = useState([]);
  const [recentBids, setRecentBids] = useState([]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
      if (window.innerWidth > 1024) {
        setIsSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset hover states when sidebar closes
  useEffect(() => {
    if (!isSidebarOpen) {
      setHoveredItem(null);
    }
  }, [isSidebarOpen]);

  // Fetch Dashboard Data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const vendorId = userInfo?.id || userInfo?.userId || 'VEN2025001';

        if (!token) {
          setError('Authentication required. Please login again.');
          setVendorData(prev => ({
            ...prev,
            vendor_name: userInfo?.name || "Vendor User",
            vendor_id: vendorId
          }));
          setLoading(false);
          return;
        }

        const apiUrl = `${API_LINK}/api/vendor/dashboard?vendorId=${vendorId}`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleLogout();
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data;

          setVendorData(data.vendor || {
            vendor_name: userInfo?.name || "Vendor",
            vendor_id: vendorId
          });

          setDashboardStats(data.stats || {
            totalBids: 0,
            activeBids: 0,
            wonBids: 0,
            lostBids: 0,
            totalTenders: 0
          });

          setActiveTenders(data.activeTenders || []);
          setRecentBids(data.recentBids || []);
        }

      } catch (error) {
        console.error('Error fetching dashboard:', error);
        setError(`Failed to load dashboard: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchDashboardData, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Handle navigation
  const handleNavigation = (page) => {
    if (onNavigate) {
      switch (page) {
        case 'Dashboard':
          onNavigate('vendor-dashboard');
          break;
        case 'Live Bidding':
          onNavigate('live-bidding');
          break;
        case 'My Bids':
          onNavigate('my-bids');
          break;
        case 'Profile':
          onNavigate('vendor-profile');
          break;
        case 'Logout':
          handleLogout();
          break;
        default:
          console.log('Unknown navigation:', page);
      }
    }
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Navigation items
  const navigationItems = [
    { name: 'Dashboard', icon: '📊', badge: null },
    { name: 'Live Bidding', icon: '📦', badge: activeTenders.length },
    { name: 'My Bids', icon: '📄', badge: dashboardStats.activeBids },
  ];

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get user initials
  const getUserInitials = (name) => {
    if (!name) return 'VE';
    const names = name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      'submitted': { bg: '#e3f2fd', color: '#1976d2' },
      'under_review': { bg: '#fff8e1', color: '#f57c00' },
      'won': { bg: '#e8f5e9', color: '#2e7d32' },
      'lost': { bg: '#ffebee', color: '#c62828' },
      'active': { bg: '#e1f5fe', color: '#0277bd' },
      'pending': { bg: '#fce4ec', color: '#c2185b' }
    };
    return colors[status] || { bg: '#f5f5f5', color: '#666' };
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={{
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      backgroundColor: '#f4f7fa',
      color: '#333',
      lineHeight: 1.6,
      minHeight: '100vh',
      width: '100%',
      overflowX: 'hidden'
    }}>
      {/* Global CSS Reset */}
      <style>
        {`
          *, *::before, *::after {
            box-sizing: border-box;
          }

          html, body {
            margin: 0;
            padding: 0;
            width: 100%;
            overflow-x: hidden;
          }
        `}
      </style>

      {/* Navigation Bar */}
      <nav style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: isMobile ? '0.75rem 1rem' : '1.5rem 4rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'fixed',
        top: 0,
        right: 0,
        left: isMobile ? 0 : '280px',
        width: isMobile ? '100%' : 'calc(100% - 280px)',
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        minHeight: isMobile ? '60px' : '140px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '0.75rem' : '2rem'
        }}>
          <img
            src="/logo.png"
            alt="Swami Vivekananda University Logo"
            style={{
              width: isMobile ? '40px' : '120px',
              height: isMobile ? '40px' : '120px',
              objectFit: 'contain'
            }}
          />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem'
          }}>
            <h1 style={{
              fontSize: isMobile ? '1rem' : '1.8rem',
              fontWeight: 'bold',
              margin: 0,
              whiteSpace: 'nowrap'
            }}>
              Swami Vivekananda University
            </h1>
            <p style={{
              fontSize: '1rem',
              margin: 0,
              opacity: 0.9,
              borderLeft: '2px solid rgba(255, 255, 255, 0.5)',
              paddingLeft: '1.5rem',
              whiteSpace: 'nowrap',
              display: isMobile ? 'none' : 'block'
            }}>
              Vendor Portal
            </p>
          </div>
        </div>

        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '8px',
            padding: '0.75rem',
            cursor: 'pointer',
            fontSize: '1.5rem',
            color: '#ffffff',
            display: isMobile ? 'block' : 'none',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
        >
          {isSidebarOpen ? '✕' : '☰'}
        </button>
      </nav>

      {/* Sidebar */}
      <aside style={{
        position: 'fixed',
        top: 0,
        left: isMobile ? (isSidebarOpen ? 0 : '-280px') : 0,
        width: '280px',
        height: '100%',
        background: '#ffffff',
        boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
        transition: 'left 0.3s ease',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto'
      }}>
        {/* Profile Section */}
        <div style={{
          background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
          color: '#ffffff',
          padding: '2.5rem 1.5rem',
          textAlign: 'center',
          borderBottom: '3px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            fontSize: '2.5rem',
            fontWeight: 'bold',
            border: '3px solid rgba(255, 255, 255, 0.4)'
          }}>
            {getUserInitials(vendorData.vendor_name)}
          </div>
          <h3 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '1.2rem',
            fontWeight: 'bold'
          }}>
            {vendorData.vendor_name}
          </h3>
          <p style={{
            margin: '0 0 0.25rem 0',
            fontSize: '0.9rem',
            opacity: 0.9
          }}>
            Vendor ID: {vendorData.vendor_id}
          </p>
          <div style={{
            display: 'inline-block',
            padding: '0.35rem 1rem',
            background: 'rgba(255, 255, 255, 0.25)',
            borderRadius: '20px',
            fontSize: '0.85rem',
            marginTop: '0.5rem'
          }}>
            {vendorData.vendor_status === 'active' ? '● Active' : '○ Inactive'}
          </div>
        </div>

        {/* Navigation Menu */}
        <nav style={{
          flex: 1,
          padding: '1.5rem 0'
        }}>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0
          }}>
            {navigationItems.map((item, index) => (
              <li key={index} style={{ margin: '0.5rem 0' }}>
                <button
                  onClick={() => handleNavigation(item.name)}
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                  style={{
                    width: '100%',
                    padding: '1rem 1.5rem',
                    background: hoveredItem === index ? 'rgba(35, 133, 72, 0.1)' : 'transparent',
                    border: 'none',
                    borderLeft: hoveredItem === index ? '4px solid #238548' : '4px solid transparent',
                    color: hoveredItem === index ? '#238548' : '#666',
                    fontSize: '1rem',
                    fontWeight: hoveredItem === index ? 'bold' : 'normal',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'left'
                  }}
                >
                  <span style={{ fontSize: '1.5rem' }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.name}</span>
                  {item.badge > 0 && (
                    <span style={{
                      background: '#238548',
                      color: '#ffffff',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout Button */}
        <div style={{ padding: '1.5rem' }}>
          <button
            onClick={() => handleNavigation('Logout')}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Main Content */}
      <main style={{
        marginLeft: isMobile ? 0 : '280px',
        marginTop: isMobile ? '60px' : '140px',
        padding: isMobile ? '1.5rem' : '2.5rem',
        minHeight: 'calc(100vh - 140px)',
        transition: 'margin-left 0.3s ease'
      }}>
        {/* Welcome Section */}
        <section style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            margin: '0 0 0.5rem 0',
            fontSize: isMobile ? '1.5rem' : '2rem',
            color: '#238548'
          }}>
            Welcome back, {vendorData.contact_person || vendorData.vendor_name}! 👋
          </h2>
          <p style={{
            margin: 0,
            color: '#666',
            fontSize: '1rem'
          }}>
            Here's an overview of your vendor activities and available tenders.
          </p>
        </section>

        {error && (
          <div style={{
            background: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
            <p style={{ margin: 0, color: '#c62828' }}>{error}</p>
          </div>
        )}

        {/* Dashboard Stats Cards */}
        <section style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Total Bids Card */}
          <div style={{
            background: '#ffffff',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'transform 0.3s ease'
          }}>
            <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>📄</div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Total Bids</h3>
            <p style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              color: '#238548',
              margin: '0.5rem 0'
            }}>
              {dashboardStats.totalBids}
            </p>
            <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
              All Time
            </p>
          </div>

          {/* Active Bids Card */}
          <div style={{
            background: '#ffffff',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'transform 0.3s ease'
          }}>
            <div style={{ fontSize: '3rem', color: '#f57c00', marginBottom: '1rem' }}>⏳</div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Active Bids</h3>
            <p style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              color: '#f57c00',
              margin: '0.5rem 0'
            }}>
              {dashboardStats.activeBids}
            </p>
            <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
              Under Review
            </p>
          </div>

          {/* Won Bids Card */}
          <div style={{
            background: '#ffffff',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center',
            cursor: 'pointer'
          }}>
            <div style={{ fontSize: '3rem', color: '#2e7d32', marginBottom: '1rem' }}>✓</div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Won Bids</h3>
            <p style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              color: '#2e7d32',
              margin: '0.5rem 0'
            }}>
              {dashboardStats.wonBids}
            </p>
            <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
              Successful
            </p>
          </div>

          {/* Lost Bids Card */}
          <div style={{
            background: '#ffffff',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center',
            cursor: 'pointer'
          }}>
            <div style={{ fontSize: '3rem', color: '#c62828', marginBottom: '1rem' }}>✕</div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Lost Bids</h3>
            <p style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              color: '#c62828',
              margin: '0.5rem 0'
            }}>
              {dashboardStats.lostBids}
            </p>
            <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
              Unsuccessful
            </p>
          </div>

          {/* Active Tenders Card */}
          <div style={{
            background: '#ffffff',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center',
            cursor: 'pointer'
          }}>
            <div style={{ fontSize: '3rem', color: '#1976d2', marginBottom: '1rem' }}>📦</div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Active Tenders</h3>
            <p style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              color: '#1976d2',
              margin: '0.5rem 0'
            }}>
              {activeTenders.length}
            </p>
            <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
              Available Now
            </p>
          </div>
        </section>

        {/* Active Tenders & Recent Bids */}
        <section style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Available Tenders */}
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              background: '#238548',
              color: '#ffffff',
              padding: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontSize: '1.8rem' }}>📦</span>
                <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Available Tenders</h3>
              </div>
              <button
                onClick={() => handleNavigation('Live Bidding')}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                View All
              </button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {activeTenders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem 0', color: '#999' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
                  <p style={{ margin: 0 }}>No active tenders available</p>
                </div>
              ) : (
                <div>
                  {activeTenders.slice(0, 3).map((tender, index) => (
                    <div key={index} style={{
                      padding: '1rem',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '0.5rem' }}>
                        {tender.title}
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '0.9rem',
                        color: '#666'
                      }}>
                        <span>Due: {formatDate(tender.submission_deadline)}</span>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: tender.vendor_has_bid ? '#e8f5e9' : '#e3f2fd',
                          color: tender.vendor_has_bid ? '#2e7d32' : '#1976d2',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: 'bold'
                        }}>
                          {tender.vendor_has_bid ? 'Submitted' : 'Open'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Bids */}
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              background: '#238548',
              color: '#ffffff',
              padding: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <span style={{ fontSize: '1.8rem' }}>📄</span>
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Recent Bid Submissions</h3>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {recentBids.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem 0', color: '#999' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
                  <p style={{ margin: 0 }}>No bid submissions yet</p>
                </div>
              ) : (
                <div>
                  {recentBids.slice(0, 5).map((bid, index) => {
                    const statusStyle = getStatusColor(bid.bid_status);
                    return (
                      <div key={index} style={{
                        padding: '1rem',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', color: '#333' }}>
                              {bid.tender_title}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                              {bid.tender_number}
                            </div>
                          </div>
                          <div style={{
                            padding: '0.25rem 0.75rem',
                            background: statusStyle.bg,
                            color: statusStyle.color,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            marginLeft: '0.5rem'
                          }}>
                            {bid.bid_status.replace(/_/g, ' ')}
                          </div>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.85rem',
                          color: '#666'
                        }}>
                          <span>{formatCurrency(bid.total_amount)}</span>
                          <span>{formatDate(bid.submission_date)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            margin: '0 0 1.5rem 0',
            fontSize: '1.3rem',
            color: '#333'
          }}>
            Quick Actions
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            <button
              onClick={() => handleNavigation('Live Bidding')}
              style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-3px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <span style={{ fontSize: '2rem' }}>📦</span>
              <span>Browse Tenders</span>
            </button>

            <button
              onClick={() => handleNavigation('My Bids')}
              style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #1976d2 0%, #64b5f6 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-3px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <span style={{ fontSize: '2rem' }}>📄</span>
              <span>My Bids</span>
            </button>

            <button
              onClick={() => handleNavigation('Profile')}
              style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-3px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <span style={{ fontSize: '2rem' }}>👤</span>
              <span>My Profile</span>
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default VendorDashboard;